import React from 'react';
import { Tv, Film, Tv2, Star, Sparkles, Trophy, Flame } from 'lucide-react';
import { useIPTV } from '../../context/IPTVContext';

export const HomeView: React.FC = () => {
  const {
    setActiveTab,
    setActiveChannelCategory,
    setActiveMovieCategory,
    setActiveSeriesCategory,
    channels,
    movies,
    series,
    currentProfile,
  } = useIPTV();

  const handleGoLive = () => {
    setActiveChannelCategory('Favoritos');
    setActiveTab('live');
  };

  const handleGoMovies = () => {
    setActiveMovieCategory('Continuar Assistindo');
    setActiveTab('movies');
  };

  const handleGoSeries = () => {
    setActiveSeriesCategory('Continuar Assistindo');
    setActiveTab('series');
  };

  return (
    <div className="flex-1 w-full h-full overflow-y-auto bg-zinc-950 p-6 md:p-10 flex flex-col items-center justify-start scrollbar-thin scrollbar-thumb-zinc-800 select-none">
      <div className="max-w-5xl w-full flex flex-col items-center text-center my-auto py-8">
        {/* OPEN TV Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-theme-accent/10 border border-theme-accent/30 text-theme-accent text-xs font-black uppercase tracking-wider mb-6 animate-pulse">
          <Sparkles className="w-3.5 h-3.5" />
          <span>Painel Inicial OPEN TV</span>
        </div>

        {/* Hero Title */}
        <h1 className="text-3xl md:text-5xl font-black text-white tracking-tight mb-4 max-w-2xl leading-tight">
          Lançamentos, Jogos &amp; Entretenimento
        </h1>
        <p className="text-sm md:text-base text-zinc-400 max-w-xl mb-10 leading-relaxed">
          Olá, <strong className="text-white">{currentProfile.name}</strong>! Este espaço será dedicado para divulgar os maiores lançamentos do cinema, grandes jogos ao vivo e eventos especiais.
        </p>

        {/* Showcase Banner / Placeholder Area */}
        <div className="w-full rounded-3xl bg-zinc-900/60 border border-zinc-800/80 p-8 md:p-12 mb-10 relative overflow-hidden backdrop-blur-xl shadow-2xl flex flex-col items-center justify-center min-h-[220px]">
          <div className="absolute -right-16 -top-16 w-64 h-64 bg-theme-accent/5 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -left-16 -bottom-16 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />

          <div className="w-14 h-14 rounded-2xl bg-zinc-800/80 border border-zinc-700/60 text-theme-accent flex items-center justify-center mb-4 shadow-lg">
            <Trophy className="w-7 h-7" />
          </div>
          <h2 className="text-lg md:text-xl font-black text-white mb-2">
            Mural de Lançamentos e Jogos da Semana
          </h2>
          <p className="text-xs md:text-sm text-zinc-400 max-w-md">
            Área reservada para trailers, próximos jogos de futebol e novidades dos catálogos.
          </p>
        </div>

        {/* Quick Hub Navigation Cards */}
        <div className="w-full grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Ao Vivo */}
          <div
            tabIndex={0}
            role="button"
            onClick={handleGoLive}
            onKeyDown={(e) => e.key === 'Enter' && handleGoLive()}
            className="p-6 rounded-2xl bg-zinc-900/70 hover:bg-zinc-850 border border-zinc-800 hover:border-theme-accent/60 transition-all duration-200 cursor-pointer text-left group shadow-lg focus:outline-none focus:ring-2 focus:ring-theme-accent"
          >
            <div className="w-12 h-12 rounded-xl bg-theme-accent/15 text-theme-accent border border-theme-accent/30 flex items-center justify-center mb-4 group-hover:scale-110 transition duration-300">
              <Tv className="w-6 h-6" />
            </div>
            <h3 className="text-base font-black text-white group-hover:text-theme-accent transition">
              Canais Ao Vivo
            </h3>
            <p className="text-xs text-zinc-400 mt-1">
              Entrar direto em Favoritos • {channels.length} canais disponíveis
            </p>
          </div>

          {/* Filmes */}
          <div
            tabIndex={0}
            role="button"
            onClick={handleGoMovies}
            onKeyDown={(e) => e.key === 'Enter' && handleGoMovies()}
            className="p-6 rounded-2xl bg-zinc-900/70 hover:bg-zinc-850 border border-zinc-800 hover:border-theme-accent/60 transition-all duration-200 cursor-pointer text-left group shadow-lg focus:outline-none focus:ring-2 focus:ring-theme-accent"
          >
            <div className="w-12 h-12 rounded-xl bg-theme-accent/15 text-theme-accent border border-theme-accent/30 flex items-center justify-center mb-4 group-hover:scale-110 transition duration-300">
              <Film className="w-6 h-6" />
            </div>
            <h3 className="text-base font-black text-white group-hover:text-theme-accent transition">
              Filmes
            </h3>
            <p className="text-xs text-zinc-400 mt-1">
              Continuar assistindo de onde parou • {movies.length} títulos
            </p>
          </div>

          {/* Séries */}
          <div
            tabIndex={0}
            role="button"
            onClick={handleGoSeries}
            onKeyDown={(e) => e.key === 'Enter' && handleGoSeries()}
            className="p-6 rounded-2xl bg-zinc-900/70 hover:bg-zinc-850 border border-zinc-800 hover:border-theme-accent/60 transition-all duration-200 cursor-pointer text-left group shadow-lg focus:outline-none focus:ring-2 focus:ring-theme-accent"
          >
            <div className="w-12 h-12 rounded-xl bg-theme-accent/15 text-theme-accent border border-theme-accent/30 flex items-center justify-center mb-4 group-hover:scale-110 transition duration-300">
              <Tv2 className="w-6 h-6" />
            </div>
            <h3 className="text-base font-black text-white group-hover:text-theme-accent transition">
              Séries
            </h3>
            <p className="text-xs text-zinc-400 mt-1">
              Retomar episódios em andamento • {series.length} séries
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
